import React from 'react';
import { WelcomeConfig } from '../../types/aiDifyCapability';
import '../../styles/chatWelcome.css';

interface PromptCard {
  readonly id: string;
  readonly text: string;
  readonly iconClass: string;
}

export interface ChatWelcomeProps {
  readonly userName?: string;
  readonly showDesignButton?: boolean;
  readonly onSelectPrompt: (text: string) => void;
  /** 来自能力配置的欢迎页数据，传入后优先使用 */
  readonly welcomeConfig?: WelcomeConfig;
  /** 能力数据是否仍在加载中，为 true 时不渲染任何内容以避免闪烁 */
  readonly capabilityLoading?: boolean;
}

/** 默认提示词：工业自动化场景 */
const DEFAULT_PROMPTS: readonly PromptCard[] = [
  {
    id: '1',
    text: '为西门子 S7-1200 编写一段电机启停的 SCL 程序',
    iconClass: 'fas fa-code',
  },
  {
    id: '2',
    text: '解释 PID 控制逻辑中的积分饱和现象及解决方案',
    iconClass: 'fas fa-wave-square',
  },
  {
    id: '3',
    text: '如何通过 Modbus TCP 协议排查通讯超时故障？',
    iconClass: 'fas fa-network-wired',
  },
];

/** 默认提示词：HMI 设计场景 */
const DEFAULT_DESIGN_PROMPTS: readonly PromptCard[] = [
  {
    id: '1',
    text: '按上-中-下的三明治结构，生成浅色系的，化妆品/轻工行业，工控系统的HMI屏幕效果，画面是表示一个小型泵站的设备',
    iconClass: 'fas fa-code',
  },
];

const DEFAULT_TITLE = '你好，工程师';
const DEFAULT_DESIGN_TITLE = 'HMI画面设计';
const DEFAULT_SUBTITLE = '使用以下工业自动化常用提示词开始，或直接在下方输入您的问题';

export const ChatWelcome: React.FC<ChatWelcomeProps> = ({
  onSelectPrompt,
  showDesignButton = false,
  welcomeConfig,
  capabilityLoading = false,
}) => {
  // ★ 数据还没加载好时，渲染一个空容器占位，避免闪烁
  if (capabilityLoading) {
    return (
      <div className="ai-loader-center">
        <div className="ai-loader">
          <svg width="100" height="100" viewBox="0 0 100 100">
            <defs>
              <mask id="clipping">
                <polygon points="0,0 100,0 100,100 0,100" fill="black"></polygon>
                <polygon points="25,25 75,25 50,75" fill="white"></polygon>
                <polygon points="50,25 75,75 25,75" fill="white"></polygon>
                <polygon points="35,35 65,35 50,65" fill="white"></polygon>
                <polygon points="35,35 65,35 50,65" fill="white"></polygon>
                <polygon points="35,35 65,35 50,65" fill="white"></polygon>
                <polygon points="35,35 65,35 50,65" fill="white"></polygon>
              </mask>
            </defs>
          </svg>
          <div className="box"></div>
        </div>
      </div>
    );
  }

  // 有配置就用配置，否则走原来的默认逻辑
  const hasConfig = welcomeConfig && (welcomeConfig.title || welcomeConfig.prompts.length > 0);

  const displayTitle: string = hasConfig && welcomeConfig.title
    ? welcomeConfig.title
    : showDesignButton
      ? DEFAULT_DESIGN_TITLE
      : DEFAULT_TITLE;

  const displaySubtitle: string = hasConfig && welcomeConfig.subtitle
    ? welcomeConfig.subtitle
    : DEFAULT_SUBTITLE;

  const displayPrompts: readonly PromptCard[] = hasConfig && welcomeConfig.prompts.length > 0
    ? welcomeConfig.prompts
    : showDesignButton
      ? DEFAULT_DESIGN_PROMPTS
      : DEFAULT_PROMPTS;

  return (
    <div className="chat-welcome-container">
      <h2 className="chat-welcome-subheader">{displayTitle}</h2>
      <p className="chat-welcome-description">{displaySubtitle}</p>

      <div className="chat-welcome-cards-grid">
        {displayPrompts.map((prompt) => (
          <div
            key={prompt.id}
            className="chat-welcome-card"
            onClick={(): void => onSelectPrompt(prompt.text)}
          >
            <span className="chat-welcome-card-text">{prompt.text}</span>
            <div className="chat-welcome-card-icon">
              <i className={prompt.iconClass} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};